import {
	DateField,
	Drawer,
	DrawerBody,
	DrawerFooter,
	DrawerFooterActions,
	DrawerHeader,
	DrawerHeading,
	FormDrawerForm,
	SubmitButton,
	TextareaField,
	TimeField,
} from "@dgs/core";
import { Formik } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GuestSelfTravelResource } from "./types/selfTravel";
import { TravelDirection } from "./types/travel";

interface Props {
	onSubmit: (formValues: GuestSelfTravelResource) => void;
	selfTravel: GuestSelfTravelResource | null;
	isRemarksFieldRequired: boolean;
	direction: TravelDirection;
}

export const SelfTravelDrawerContent: FC<Props> = ({ onSubmit, selfTravel, isRemarksFieldRequired, direction }) => {
	const { t } = useTranslation();

	const initialValues: GuestSelfTravelResource = selfTravel || {
		time: "",
		date: "",
		remarks: "",
	};

	const validate = useCallback(
		(values: GuestSelfTravelResource) => {
			const errors: { [T in keyof GuestSelfTravelResource]?: string } = {};

			if (!values.time) {
				errors.time = t("Field is required");
			}
			if (!values.date) {
				errors.date = t("Field is required");
			}

			if (!values.remarks && isRemarksFieldRequired) {
				errors.remarks = t("Field is required");
			}

			return errors;
		},
		[isRemarksFieldRequired, t],
	);

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
			<FormDrawerForm>
				<DrawerBody>
					<DateField name="date" label={direction === "arrival" ? t("Arrival date") : t("Departure date")} required />
					<TimeField name="time" label={direction === "arrival" ? t("Arrival time") : t("Departure time")} required />
					<TextareaField name="remarks" label={t("Remarks")} required={isRemarksFieldRequired} />
				</DrawerBody>
				<DrawerFooter>
					<DrawerFooterActions />
					<SubmitButton>{t("Save")}</SubmitButton>
				</DrawerFooter>
			</FormDrawerForm>
		</Formik>
	);
};

interface DrawerProps {
	selfTravel: GuestSelfTravelResource;
	open: boolean;
	handleClose: () => void;
	onSubmit: (formValues: GuestSelfTravelResource) => void;
	isRemarksFieldRequired: boolean;
	direction: TravelDirection;
}

export const SelfTravelEditDrawer: FC<DrawerProps> = ({
	open,
	handleClose,
	selfTravel,
	onSubmit,
	isRemarksFieldRequired,
	direction,
}) => {
	const { t } = useTranslation();

	const handleSubmit = useCallback(
		(values: GuestSelfTravelResource) => {
			onSubmit(values);
			handleClose();
		},
		[handleClose, onSubmit],
	);

	if (!selfTravel) return null;

	return (
		<Drawer open={open} onClose={handleClose}>
			<DrawerHeader onClose={handleClose}>
				<DrawerHeading>{t("Edit self travel")}</DrawerHeading>
			</DrawerHeader>
			<SelfTravelDrawerContent
				onSubmit={handleSubmit}
				selfTravel={selfTravel}
				isRemarksFieldRequired={isRemarksFieldRequired}
				direction={direction}
			/>
		</Drawer>
	);
};
